import React, { useState, useEffect, useRef } from "react";
import { Badge, Checkbox, Space, Switch, Tabs, Typography, Row, Col, Card, Table, Progress, Tooltip, Modal, Button, Divider } from "antd";
import dayjs from "dayjs";
import ExportarExcelPoa from "./ExportarExcelPoa"; // Importamos el componente de exportación
import PoaResumenFactores from "./PoaResumenFactores"; // Importamos el componente de resumen de factores
import PoaResumenTipoActividad from "./PoaResumenTipoActividad"; // Importamos el componente de resumen por tipo de actividad
import {
    SearchOutlined, FileImageOutlined, FileDoneOutlined,
    FileProtectOutlined,
    FileSearchOutlined,
    DotChartOutlined
} from "@ant-design/icons";
import './TablaResumenAvance.css';
import AvanceProyectosChart from './Graficos/AvanceProyectosChart';
import AvanceProyectosChartBarras from './Graficos/AvanceProyectosChartBarras';
import { getPOPesoResumenCompleto } from '../../services/plaOperativo/headerPlanOperativoServices';
import { getPOAll } from '../../services/plaOperativo/headerPlanOperativoServices';
import html2canvas from "html2canvas";
import ExportarPOExcel from "./Exportarpoexcel";


const PoaResumen = ({ data }) => {

    // Estado de la tabla
    const [filteredData, setFilteredData] = useState([]);
    const tableRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
    const [resumen, setResumen] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [compacto, setCompacto] = useState(false);
    const [activeKey, setActiveKey] = useState("proyectos");


    const [visibleGrafico, setVisibleGrafico] = useState(false);
    const [mostrarFechas, setMostrarFechas] = useState(true);

    const [visibleModal, setVisibleModal] = useState(false);
    const [selectedChart, setSelectedChart] = useState(null);

    const { Text } = Typography;

    const { TabPane } = Tabs;


    const conversionData = [
        { rango: "0% - 50%", color: "#f20013", descripcion: "Bajo" },
        { rango: "51% - 75%", color: "#ffa500", descripcion: "Medio" },
        { rango: "76% - 100%", color: "#198754", descripcion: "Alto" }
    ];

    // Función para alternar la visibilidad
    const toggleFechas = () => {
        setMostrarFechas(!mostrarFechas);
    };


    // Mapea los campos SQL a nuestro formato de tabla
    useEffect(() => {
        if (!Array.isArray(data)) return;
        const mapped = data.map((p) => {
            // 1) Convertimos a número (por si llega cadena)
            const meta = Number(p.total_meta);
            const sumaAvances = p.total_meta_ejecutada;

            return {
                key: p.oid,
                oid: p.oid,
                codigo: p.codigo_interno,
                codigopre: p.codPre,
                nombre: p.nombre_profesional_gse,
                grupoterritorial: p.grupoterritorial,
                implementador: p.implementador,
                fechaAsignacion: p.fechaAsignacion,
                total_meta: meta,
                // Aquí sobreescribimos “total_meta_ejecutada” con nuestra suma real:
                total_meta_ejecutada: sumaAvances,
                fecha_actualizacion: p.fecha_actualizacion,
                estado: p.estado

                // Opcional: podrías incluso calcular aquí mismo los porcentajes si quisieras
                // porcentaje_avance: meta > 0 ? Math.min((sumaAvances / meta) * 100, 100) : 0,
                // porcentaje_acumulado: meta > 0 ? (sumaAvances / meta) * 100 : 0,
            };
        });

        setFilteredData(mapped);
    }, [data]);



    // 2) Hook para llamar al servicio cuando el componente monte
    useEffect(() => {
        const fetchResumen = async () => {
            setLoading(true);
            try {
                const res = await getPOAll();           // llamas al servicio sin parámetros

                if (res.status === 200 && res.data) {
                    // suponemos que res.data tiene la forma { proyecto: ..., detalle: [...] }
                    setResumen(res.data);
                } else {
                    console.warn('Servicio respondió con status:', res.status);
                }
            } catch (err) {
                console.error('Error al obtener el resumen completo:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchResumen();
    }, []); // <-- arreglo vacío para que sólo se ejecute una vez al montar


    // Función para abrir el modal y setear los datos
    // 2) Hacemos verDetalles async para poder await
    const verDetalles = async (record) => {
        // 1) Llamas al servicio y obtienes { proyecto, detalle }

        const res = await getPOPesoResumenCompleto(record.oid);
        if (res.status !== 200 || !res.success) return;
        const { proyecto, detalle } = res.data;

        const fechaMasReciente = record.fecha_actualizacion; // p.ej. "2025-04-01T05:00:00.000Z"

        //Calculamos el avance general del proyecto
        const totalMeta = proyecto.total_meta || 0;
        const totalMetaEjecutada = proyecto.total_meta_ejecutada || 0;
        const avanceGeneral = totalMeta > 0
            ? ((totalMetaEjecutada / totalMeta) * 100).toFixed(1)
            : 0;

        // 4) Guardas todo en el estado, incluyendo la fecha de actualización
        setProyectoSeleccionado({
            ...record,
            proyecto,
            detalle,
            fechaActualizacionAvances: fechaMasReciente,
            avanceGeneral: avanceGeneral,
            estado: record.estado,
        });
        setVisible(true);
    };


    // Función para cerrar el modal
    const cerrarModal = () => {
        setVisible(false);
        setProyectoSeleccionado(null);
    };



    const onChangeSwitch = checked => {

        setCompacto(checked);
    };

    const formatFecha = fecha =>
        fecha
            ? dayjs(fecha).format('DD-MM-YYYY')
            : 'N/A';

    function getColorByPct(pct) {
        // Asegúrate de que pct sea número
        pct = Number(pct) || 0;


        for (let i = 0; i < conversionData.length; i++) {
            const { rango, color } = conversionData[i];
            // extraemos min y max como floats
            let [minStr, maxStr] = rango.split("-").map(s => s.replace("%", "").trim());
            const min = parseFloat(minStr);
            const max = parseFloat(maxStr);

            if (i < conversionData.length - 1) {
                // límite superior abierto: < próximo.min
                const nextMin = parseFloat(
                    conversionData[i + 1].rango
                        .split("-")[0]
                        .replace("%", "")
                        .trim()
                );
                if (pct >= min && pct < nextMin) {

                    return color;
                }
            } else {
                // último rango es cerrado
                if (pct >= min && pct <= max) {

                    return color;
                }
            }
        }


        return "#ddd";
    }

    // Columnas de la tabla
    const columns = [

        ...(mostrarFechas
            ? [
                {
                    title: "",
                    key: "detalle",
                    align: "center",
                    width: 200,
                    render: (_, record) => {
                        const isDisabled = record.estado === 0;

                        return (
                            <>
                                                        <Tooltip title={isDisabled ? "POA inactivo" : "Ver detalles"}>
                                <SearchOutlined
                                    className="icono-lupa"
                                    style={{
                                        fontSize: 18,
                                        color: isDisabled ? "#ccc" : "#1677ff",
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        opacity: isDisabled ? 0.5 : 1,
                                    }}
                                    onClick={isDisabled ? undefined : () => verDetalles(record)}
                                />
                            </Tooltip>
                            
                            {/* Aquí llamas al componente ExportarPOExcel */}
                            <ExportarPOExcel oid_proyecto={record.oid} />
                            </>

                        );
                    },
                },


            ]
            : []),

        {
            title: (
                <span style={{ whiteSpace: 'nowrap' }}>
                    Proyecto&nbsp;
                    <Checkbox checked={mostrarFechas} onChange={toggleFechas} />
                </span>
            ),
            key: "codigoProyecto",
            width: 270,
            align: "left",
            ellipsis: true,
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            onCell: () => ({
                style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            }),
            render: (_, record) => `${record.codigo} - ${record.implementador}`
        },

        ...(mostrarFechas
            ? [
                {
                    title: <span style={{ whiteSpace: 'nowrap' }}>Grupo Territorial</span>,
                    dataIndex: 'grupoterritorial',
                    key: 'grupoTerritorial',
                    ellipsis: true,
                    width: 250,
                    onHeaderCell: () => ({
                        style: { whiteSpace: 'nowrap' }
                    }),
                    onCell: () => ({
                        style: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }),
                },
                {
                    title: "Fecha Actualización",
                    dataIndex: "fecha_actualizacion",
                    key: "fecha_actualizacion",
                    align: "center",
                    width: 170,
                    ellipsis: true,
                    render: (d) => (d ? dayjs(d).format("DD-MM-YYYY") : "-"),
                },



            ]
            : []),

        {
            title: "Meta",
            dataIndex: "total_meta",
            key: "total_meta",
            align: "center", // Alineación centrada
            width: 100,
            render: (text) => (
                <Progress
                    percent={100} // La barra siempre estará llena
                    format={() => (
                        <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                            {`${Math.round(parseFloat(text))}`}
                        </span>
                    )} // Muestra el valor real
                    percentPosition={{ align: 'right', type: 'inner' }}
                    size={[90, 20]}
                    strokeColor={{ '0%': '#108ee9', '100%': '#108ee9' }} // Degradado de colores
                />
            )
        },

        {
            title: <span style={{ whiteSpace: 'nowrap' }}>Avance Ejecutado</span>,
            dataIndex: "total_meta_ejecutada",
            key: "total_meta_ejecutada",
            align: "center",
            ellipsis: true,
            width: 130,
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            onCell: () => ({
                style: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }
            }),
            render: (text) => {
                const num = Number(text);
                // si no es número, mostramos guión
                const formatted =
                    isNaN(num)
                        ? '-'
                        : num.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                        });
                return (
                    <Progress
                        percent={100}
                        format={() => (
                            <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                                {formatted}
                            </span>
                        )}
                        percentPosition={{ align: 'right', type: 'inner' }}
                        size={[90, 20]}
                        strokeColor={{ '0%': '#108ee9', '100%': '#108ee9' }}
                    />
                );
            }
        },



        {
            title: "Avance Ejecutado (%)",
            dataIndex: "total_meta_ejecutada",
            key: "total_meta_ejecutada",
            width: 180,
            align: "center",
            render: (text, record) => {
                const ejecutado = parseFloat(text);
                const programado = parseFloat(record.total_meta);


                if (isNaN(ejecutado) || isNaN(programado) || programado === 0) {
                    return <span>-</span>;
                }

                // calculamos porcentaje
                const porcentaje = parseFloat(((ejecutado / programado) * 100).toFixed(1));
                // elegimos color
                let strokeColor = "#f20013";
                if (porcentaje > 75) strokeColor = "#198754";
                else if (porcentaje > 50) strokeColor = "#ffa500";

                return (
                    <Progress
                        percent={porcentaje}
                        // aquí va tu número con 2 decimales


                        format={() => (
                            <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                                {porcentaje.toFixed(1)}%
                            </span>
                        )}
                        // ¡este es el truco para mostrarse dentro!
                        percentPosition={{ align: 'right', type: 'inner' }}
                        strokeColor={strokeColor}
                        size={[150, 20]}
                    />
                );
            }
        }




    ];

    // Definición de columnas para la tabla dentro del modal
    const columnasModal = [
        ...(compacto
            ? [
                {
                    title: "Actividad",
                    key: "actividad",
                    dataIndex: "actividad",
                    width: 250,
                    render: actividad => (
                        <span>
                            {actividad.codigo} – {actividad.descripcion}
                        </span>
                    )
                },
                {
                    title: "Tarea",
                    key: "tarea",
                    dataIndex: "tarea",
                    width: 250,
                    render: tarea => (
                        <span>
                            {tarea.codigo} – {tarea.descripcion}
                        </span>
                    )
                },
            ] : []),

        {
            title: "Indicador",
            key: "indicador",
            width: 350,
            dataIndex: ["indicador", "texto"],
        },
        {
            title: "Meta",
            key: "meta",
            dataIndex: ["indicador", "meta"],
            width: 100,

            align: "center",
            render: meta => {
                const n = parseFloat(meta);
                if (isNaN(n)) return "-";
                return n % 1 === 0 ? n : n.toFixed(2);
            }
        },
        {
            title: "Meta Ejecutada",
            key: "meta_ejecutada",
            dataIndex: ["indicador", "meta_ejecutada"],
            align: "center",
            width: 150,

            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (me) => {
                const n = parseFloat(me);
                if (isNaN(n)) return "-";
                return n.toLocaleString("es-CO", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            }
        },
        {
            title: "Avance (%)",
            key: "avance",
            align: "center",
            width: 120,
            render: (_, record) => {
                const meta = parseFloat(record.indicador.meta);
                const ejec = parseFloat(record.indicador.meta_ejecutada);
                if (isNaN(meta) || meta === 0) return "-";

                const pctRaw = (ejec / meta) * 100;
                const pct = parseFloat(pctRaw.toFixed(1)); // un decimal
                const strokeColor = getColorByPct(pct);

                return (
                    <Progress
                        percent={pct}
                        size={[100, 20]}
                        strokeColor={strokeColor}
                        percentPosition={{ align: "right", type: "inner" }}
                        format={() => (
                            <span style={{ fontWeight: "bold", fontSize: 14, color: "#FFF" }}>
                                {`${pct}%`}
                            </span>
                        )}
                    />
                );
            }
        },
        ...(compacto
            ? [
                {
                    title: "Fecha Inicio",
                    key: "fecha_inicio",
                    dataIndex: ["indicador", "fecha_inicio"],
                    align: "center",
                    width: 150,
                    onHeaderCell: () => ({
                        style: { whiteSpace: 'nowrap' }
                    }),
                    render: formatFecha
                },

                {
                    title: "Fecha Fin",
                    key: "fecha_fin",
                    dataIndex: ["indicador", "fecha_fin"],
                    align: "center",
                    width: 150,
                    onHeaderCell: () => ({
                        style: { whiteSpace: 'nowrap' }
                    }),
                    render: formatFecha
                },
                {
                    title: "Tipo de Actividad",
                    key: "tipo_actividad",
                    dataIndex: ["indicador", "tipo_actividad"],
                    onHeaderCell: () => ({
                        style: { whiteSpace: 'nowrap' }
                    }),
                    width: 150,
                },
                {
                    title: "Actor",
                    key: "actor",
                    dataIndex: ["indicador", "actor"],
                    onHeaderCell: () => ({
                        style: { whiteSpace: 'nowrap' }
                    }),
                    width: 200,
                },
            ] : []),


    ];


    const capturarTabla = async () => {
        if (tableRef.current) {
            const canvas = await html2canvas(tableRef.current);
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = "Porcentajes_de_Avance_POAs.png";
            link.click();
        }
    };


    // Función para abrir el modal y establecer qué gráfico se maximiza
    const handleOpenModal = (chartType) => {
        setSelectedChart(chartType);
        setVisibleModal(true);
    };

    // en la parte superior de tu componente

    const operations = (
        <>
            {!mostrarFechas ? (
                <Button type="primary" icon={<FileImageOutlined />} onClick={capturarTabla} style={{ marginRight: '8px', backgroundColor: "#28a745", borderColor: "#218838", color: "white", marginBottom: "10px" }}>
                    Imagen Tabla
                </Button>
            ) : null}
            <ExportarExcelPoa dataSource={filteredData} todosLosDatos={data} />
        </>
    );

    const tabItems = [
        {
            key: 'proyectos',
            label: (
                <span>
                    <FileDoneOutlined />
                    Proyectos
                </span>
            ),
            children: (
                <div
                    ref={tableRef}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        overflowX: "auto",
                    }}
                >
                    <Table
                        columns={columns}
                        dataSource={filteredData}
                        pagination={false}
                        className="tabla-resumen-avance"
                        style={{
                            marginBottom: "2em",
                            width: mostrarFechas ? "100%" : "60%",
                            maxWidth: "100%",
                            minWidth: "600px",
                            transition: "width 0.3s ease-in-out",
                        }}
                        size="middle"
                    />
                </div>
            ),
        },
        {
            key: 'factores',
            label: (
                <span>
                    <FileProtectOutlined />
                    Factores
                </span>
            ),
            children: <PoaResumenFactores data={resumen} />,
        },
        {
            key: 'tipoActividad',
            label: (
                <span>
                    <FileSearchOutlined />
                    Tipo Actividad
                </span>
            ),
            children: <PoaResumenTipoActividad data={resumen} />,
        },
        {
            key: 'graficos',
            label: (
                <span>
                    <DotChartOutlined />
                    Gráficos
                </span>
            ),
            children: (
                <div className="container-fluid">
                    <div className="row">
                        <div
                            className="col-12 col-md-6 d-flex flex-column align-items-center chart-container mb-5 pt-2"
                            style={{ width: "100%", maxWidth: "49%", boxShadow: "none", cursor: "default" }}
                        >
                            <Button type="primary" onClick={() => handleOpenModal("barras")} style={{ padding: 5 }}>
                                Maximizar
                            </Button>
                            <AvanceProyectosChartBarras data={filteredData} />
                        </div>
                        <div
                            className="col-12 col-md-6 d-flex flex-column align-items-center chart-container mb-5 pt-2"
                            style={{ width: "100%", maxWidth: "49%", boxShadow: "none", cursor: "default" }}
                        >
                            <Button type="primary" onClick={() => handleOpenModal("radar")} style={{ padding: 5 }}>
                                Maximizar
                            </Button>
                            <AvanceProyectosChart data={filteredData} />
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    if (loading) return <p>Cargando datos…</p>;
    if (error) return <p>Error al cargar: {error.message}</p>;


    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 0 }}>
                <h2 style={{ margin: 0 }}>Avance POA´s</h2>
            </div>



            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: 0 }}>
                <Space size="large">
                    {conversionData.map((item) => (
                        <Badge
                            key={item.rango}
                            color={item.color}
                            text={`${item.rango} (${item.descripcion})`}
                        />
                    ))}
                </Space>
            </div>

            <Divider />


            <Tabs
                activeKey={activeKey}
                onChange={setActiveKey}
                type="card"
                size="large"
                items={tabItems}
                tabBarExtraContent={operations}
            />

            <Modal
                title="Avance POA´s"
                open={visibleModal}
                onCancel={() => setVisibleModal(false)}
                footer={null}
                width={"90vw"}
                style={{ top: 30 }}
            >
                <div
                    style={{
                        width: "100%",
                        height: "80vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start", // Alinea arriba
                        overflow: "hidden",
                        paddingTop: "20px", // Espaciado superior opcional
                    }}
                >

                    <div
                        style={{
                            width: "80%",
                            height: "80%",
                            maxWidth: selectedChart === "radar" ? "600px" : "100%",
                            maxHeight: "500px",
                            marginTop: "0", // Asegura que la gráfica esté en la parte superior
                        }}
                    >
                        {selectedChart === "barras" ? (
                            <AvanceProyectosChartBarras data={filteredData} />
                        ) : selectedChart === "radar" ? (
                            <AvanceProyectosChart data={filteredData} />
                        ) : null}
                    </div>
                </div>
            </Modal>




            {/* Modal con la tabla de detalles */}
            <Modal
                title={`Detalles del Proyecto: ${proyectoSeleccionado?.codigo || ""} - ${proyectoSeleccionado?.implementador || ""}`}
                open={visible}
                onCancel={cerrarModal}
                modalStyle={{ top: 30 }} // Ajusta la posición
                style={{ top: 30 }} // Ajusta la posición
                footer={[
                    <Button key="close" onClick={cerrarModal}>
                        Cerrar
                    </Button>,
                ]}
                width={'80%'}
            >
                <div>
                    <Card style={{ background: "#f5f5f5", borderRadius: 10, padding: "15px" }}>
                        <Row gutter={[16, 8]}>
                            <Col span={24}>
                                <Badge.Ribbon text="Inicio" color="blue">
                                    <Card size="small">
                                        <Text strong>Fecha Inicio POA: </Text>

                                        {formatFecha(proyectoSeleccionado?.proyecto.fechaAsignacion) || "N/A"}

                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="Terminación" color="red">
                                    <Card size="small">
                                        <Text strong>Fecha Terminación POA: </Text>

                                        {formatFecha(proyectoSeleccionado?.proyecto.fechaTerminacion) || "N/A"}

                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="Última actualización" color="green">
                                    <Card size="small">
                                        <Text strong>Fecha de actualización de Avances:</Text>{' '}
                                        {proyectoSeleccionado?.fechaActualizacionAvances
                                            ? (() => {
                                                const d = new Date(proyectoSeleccionado.fechaActualizacionAvances);
                                                const dd = String(d.getDate()).padStart(2, '0');
                                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                                const yyyy = d.getFullYear();
                                                return `${dd}-${mm}-${yyyy}`;
                                            })()
                                            : 'N/A'}
                                    </Card>

                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="% de Avance general:" color={getColorByPct(proyectoSeleccionado?.avanceGeneral || 0)}>
                                    <Card size="small">
                                        <Text strong>% de Avance: </Text>
                                        <Progress
                                            percent={proyectoSeleccionado?.avanceGeneral || 0}
                                            size={[100, 20]}
                                            strokeColor={getColorByPct(proyectoSeleccionado?.avanceGeneral || 0)}
                                            percentPosition={{ align: 'right', type: 'inner' }}
                                            format={() => (
                                                <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                                                    {`${proyectoSeleccionado?.avanceGeneral || 0}%`}
                                                </span>
                                            )}
                                            style={{ width: "100px", marginLeft: "10px", position: "absolute", top: "0px" }}
                                        />
                                    </Card>
                                </Badge.Ribbon>
                            </Col>

                        </Row>
                    </Card>
                </div>


                <div className="d-flex align-items-center justify-content-between mt-2">
                    <Space size="large">
                        <span style={{ fontWeight: 'bold' }}>
                            {compacto ? 'Extendido' : 'Compacto'}
                        </span>
                        <Switch
                            checked={compacto}
                            onChange={onChangeSwitch}
                            checkedChildren="E"
                            unCheckedChildren="C"
                        />
                    </Space>
                    <Space size="large">
                        {conversionData.map((item) => (
                            <Badge
                                key={item.rango}
                                color={item.color}
                                text={`${item.rango} (${item.descripcion})`}
                            />
                        ))}
                    </Space>
                </div>


                <Divider />

                <Table
                    columns={columnasModal}
                    dataSource={proyectoSeleccionado?.detalle || []}
                    rowKey={record => `act${record.actividad.codigo}-tar${record.tarea.id}-ind${record.indicador.id}`}
                    size="middle"
                    pagination={false}
                    className="tabla-resumen-avance"
                    scroll={{
                        x: 1000,    // ahora que cada columna tiene anchura fija, true activa scroll horizontal automático
                        y: 600      // si quieres también scroll vertical
                    }}
                    style={{
                        marginBottom: "2em",
                        transition: "width 0.3s ease-in-out",
                    }}
                />

            </Modal>


        </div>
    );
};

export default PoaResumen;
